import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, Zap, Truck, BadgeCheck } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1560769629-975ec94e6a86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxzbmVha2VycyUyMHdoaXRlJTIwYmFja2dyb3VuZCUyMHN0dWRpbyUyMHByb2R1Y3QlMjBwaG90b2dyYXBoeXxlbnwwfHx8fDE3OTEyNDMwMzN8MA&ixlib=rb-4.1.0&q=85";

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 60, damping: 15 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-16, 16]), { stiffness: 60, damping: 15 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="inicio"
      data-testid="vult-hero-section"
      ref={ref}
      onMouseMove={onMove}
      className="relative flex min-h-screen items-center overflow-hidden bg-white pt-16"
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(#0F172A 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      <div className="pointer-events-none absolute -right-24 top-1/2 hidden -translate-y-1/2 select-none font-display text-[26rem] font-black italic leading-none text-stroke-ink opacity-[0.06] lg:block">
        VULT
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-900 px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-widest"
          >
            <Zap size={12} className="text-vult-red" /> Drop raro &amp; sneakers de elite
          </motion.div>

          <h1 className="font-display text-6xl font-black uppercase leading-[0.9] tracking-tight text-slate-900 sm:text-7xl lg:text-8xl">
            <MaskedLine delay={0.25}>Novos tênis,</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="bg-vult-lime px-3 italic">novos passos.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75 }}
            className="mt-6 max-w-md text-base leading-relaxed text-slate-600"
          >
            Trabalhamos com tênis diretamente da China, verificados um a um
            garantindo qualidade e custo benefício!
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="vult-hero-shop-cta"
              href="#catalogo"
              className="group inline-flex items-center gap-3 rounded-full bg-vult-red px-8 py-4 font-mono text-sm font-bold uppercase tracking-widest text-slate-900 transition-all hover:bg-slate-900 hover:text-white"
            >
              Comprar agora
              <ArrowDown size={16} className="transition-transform group-hover:translate-y-1" />
            </a>
            <div className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-500">
              <Truck size={14} className="text-vult-blue" /> Frete grátis +R$ 799
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="mt-12 flex gap-10 border-t border-slate-200 pt-6"
          >
            {[["Importados", "direto da China 🇨🇳"], ["Qualidade", "altíssima"], ["Envio", "para todo o Brasil"]].map(([n, l]) => (
              <div key={l}>
                <div className="font-display text-3xl font-black text-slate-900">{n}</div>
                <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative flex items-center justify-center" style={{ perspective: 1000 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              <div className="absolute inset-8 rounded-full bg-vult-lime blur-3xl opacity-40" />
              <img
                src={HERO_IMG}
                alt="Nike Air Max em destaque"
                className="relative z-10 w-full max-w-lg rounded-3xl mix-blend-multiply drop-shadow-2xl"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.2, type: "spring" }}
              className="absolute -left-4 top-6 z-20 rounded-2xl border-2 border-slate-900 bg-white px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest shadow-[4px_4px_0_#0F172A]"
            >
              Edition 01/50
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.35, type: "spring" }}
              className="absolute -right-2 bottom-10 z-20 flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-vult-lime shadow-[4px_4px_0_#FFD600]"
            >
              <BadgeCheck size={14} /> Qualidade Altíssima
            </motion.div>
            <div className="absolute -bottom-6 -left-8 z-20 hidden animate-spin-slow sm:block">
              <svg width="110" height="110" viewBox="0 0 100 100">
                <defs>
                  <path id="circlePath" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
                </defs>
                <text className="fill-slate-900 font-mono text-[10.5px] font-bold uppercase" style={{ letterSpacing: "2.5px" }}>
                  <textPath href="#circlePath">vult • sneakers de elite • vult •</textPath>
                </text>
                <circle cx="50" cy="50" r="10" fill="#FFD600" />
              </svg>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
